import { useCallback } from 'react';
import {
  Avatar,
  Button,
  Stack,
  Inline,
  InlineItem,
  Secondary,
  Text,
} from '@kinesis/bungle';
import { noop } from 'lodash/fp';
import { ShareInvitationData } from '@/types';
import { parseISO } from 'date-fns/fp';
import { formatDistance } from '@/date';

// A pending disclosure share, alongside the organisation invitation rows.
// Accepting opens the disclosure itself rather than joining anything.
const PendingShareRow = (props: PendingShareRowProps) => {
  const {
    onAccept = noop,
    onDecline = noop,
    invitation: { token, label, sharedBy, expiresAt, ownerName, avatar },
  } = props;

  const expiresIn = formatDistance(new Date(), parseISO(expiresAt));

  const handleAccept = useCallback(() => {
    onAccept(token);
  }, [onAccept, token]);

  const handleDecline = useCallback(() => {
    onDecline(token);
  }, [onDecline, token]);

  return (
    <Inline space='small'>
      {/* The owning organisation's logo, as the organisation rows render
          theirs. A row with no resolved owner renders the generic
          placeholder initial instead — the avatar is never skipped. */}
      <InlineItem>
        <Avatar
          alt={ownerName ? `${ownerName} logo` : 'Organisation logo'}
          image={avatar}
          magnitude='small'
          variant='organisation'
        >
          {ownerName?.slice(0, 1) ?? 'I'}
        </Avatar>
      </InlineItem>
      <InlineItem sizing='fill-container'>
        <Stack>
          <Text>{label}</Text>
          <Secondary>
            {/* Diplomat answers an empty issuer name when the issuer isn't a
                resolvable user. The trailing kind is there because the list
                mixes organisation memberships and disclosure shares. */}
            Invited by {sharedBy || 'someone'} (expires {expiresIn}) ·
            Disclosure
          </Secondary>
        </Stack>
      </InlineItem>
      <InlineItem>
        <Inline space='small'>
          <InlineItem>
            <Button
              variant='secondary'
              display='block'
              magnitude='small'
              onClick={handleDecline}
            >
              Decline
            </Button>
          </InlineItem>
          <InlineItem>
            <Button variant='action' magnitude='small' onClick={handleAccept}>
              Accept
            </Button>
          </InlineItem>
        </Inline>
      </InlineItem>
    </Inline>
  );
};

export type PendingShareRowProps = {
  onAccept?: (token: string) => void;
  onDecline?: (token: string) => void;
  invitation: ShareInvitationData;
};

export { PendingShareRow };
